import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { MapPin, ExternalLink, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface Venue {
  id: string;
  name: string;
  address: string;
  phone?: string;
  website?: string;
  routePlanner?: string;
}

interface VenueCardProps {
  venue: Venue;
  index: number;
  lang?: 'en' | 'fr' | 'de' | 'it';
}

interface GeocodedLocation {
  lat: number;
  lng: number;
}

const VenueCard: React.FC<VenueCardProps> = ({ venue, index, lang = 'en' }) => {
  const [location, setLocation] = useState<GeocodedLocation | null>(null);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      openInMaps: 'Open in Google Maps',
      visitWebsite: 'Visit Website',
      loading: 'Loading...'
    },
    fr: {
      openInMaps: 'Ouvrir dans Google Maps',
      visitWebsite: 'Visiter le site',
      loading: 'Chargement...'
    },
    de: {
      openInMaps: 'In Google Maps öffnen',
      visitWebsite: 'Website besuchen',
      loading: 'Laden...'
    },
    it: {
      openInMaps: 'Apri in Google Maps',
      visitWebsite: 'Visita il sito',
      loading: 'Caricamento...'
    }
  };

  const t = translations[lang];

  useEffect(() => {
    const geocodeVenue = async () => {
      // Check if we have cached location
      const cacheKey = `geocode_${venue.address}`;
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const parsedCache = JSON.parse(cached);
          const cacheAge = Date.now() - parsedCache.timestamp;
          // Cache for 30 days
          if (cacheAge < 30 * 24 * 60 * 60 * 1000) {
            setLocation(parsedCache.location);
            setLoading(false);
            return;
          }
        } catch (e) {
          // Invalid cache, continue to geocode
        }
      }

      // Stagger requests to respect rate limits
      await new Promise(resolve => setTimeout(resolve, index * 1100));

      try {
        // Clean up the address
        let cleanAddress = venue.address.replace(/,\s*Swiss\s*$/i, '').trim();
        if (!cleanAddress.match(/Switzerland$/i)) {
          cleanAddress = `${cleanAddress}, Switzerland`;
        }

        const url = `https://nominatim.openstreetmap.org/search?` +
          `format=json&q=${encodeURIComponent(cleanAddress)}&limit=1`;

        const response = await fetch(url, {
          headers: {
            'User-Agent': 'TTStats.ch'
          }
        });

        const data = await response.json();

        if (data && data.length > 0) {
          const geocoded = {
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon)
          };
          setLocation(geocoded);

          // Cache the result
          localStorage.setItem(cacheKey, JSON.stringify({
            location: geocoded,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error(`Failed to geocode venue ${venue.name}:`, error);
      } finally {
        setLoading(false);
      }
    };

    geocodeVenue();
  }, [venue, index]);

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
    >
      {/* Map Section */}
      <div className="relative h-48 bg-gray-100 dark:bg-gray-700 transition-colors duration-300">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 transition-colors duration-300">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400 mb-2 transition-colors duration-300"></div>
              <p className="text-gray-500 dark:text-gray-400 text-xs transition-colors duration-300">{t.loading}</p>
            </div>
          </div>
        ) : location ? (
          <MapContainer
            center={[location.lat, location.lng]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={false}
            dragging={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[location.lat, location.lng]}>
              <Popup>
                <div className="text-xs">
                  <p className="font-semibold">{venue.name}</p>
                  <p className="text-gray-600">{venue.address}</p>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 transition-colors duration-300">
            <div className="text-center text-gray-400 dark:text-gray-500 transition-colors duration-300">
              <MapPin className="h-8 w-8 mx-auto mb-2" />
              <p className="text-xs">Location unavailable</p>
            </div>
          </div>
        )}

        {/* Overlay click to open in Google Maps */}
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 cursor-pointer group z-10"
        >
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          <div className="absolute top-2 right-2 bg-white dark:bg-gray-800 px-2 py-1 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300">
            <ExternalLink className="h-3 w-3 text-gray-700 dark:text-gray-300 transition-colors duration-300" />
          </div>
        </a>
      </div>

      {/* Info Section */}
      <div className="p-3">
        <div className="mb-3">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1.5 transition-colors duration-300">{venue.name}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5 transition-colors duration-300">
            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
            <span>{venue.address}</span>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-800/40 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium transition-colors duration-300"
          >
            <MapPin className="h-3.5 w-3.5" />
            {t.openInMaps}
          </a>

          {venue.website && (
            <a
              href={venue.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-2.5 py-1.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-xs font-medium transition-colors duration-300"
              title={t.visitWebsite}
            >
              <Globe className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default VenueCard;
